import { fetchStaticJson, resolveAppStaticUrl } from '../../config/fetchStaticJson';
import { getBotRoutePath } from '../../config/getBotConfig';
import { badgeIconFromFlags } from './badgeIcon';

export const BOTS_LIST_FILE_NAME = 'botsList.json';
export const BOTS_IN_VC_FILE_NAME = 'botsInVC.json';

function botsInVcUrl() {
  return resolveAppStaticUrl(
    window.config?.botsInVC,
    'https://mydev.edu.gov.il/bucketfiles/myEduWidget/jsons/botsInVC.json'
  );
}

export async function fetchTeachingAssistantBotsList() {
  const data = await fetchStaticJson(botsInVcUrl());
  return Array.isArray(data) ? data : [];
}

export async function fetchBotsInVcForSave(fallbackList) {
  try {
    const latest = await fetchTeachingAssistantBotsList();
    if (latest.length > 0) return latest;
  } catch {
    // use in-memory or local copy
  }
  if (Array.isArray(fallbackList) && fallbackList.length > 0) {
    return fallbackList;
  }
  try {
    const local = await fetchStaticJson(resolveAppStaticUrl('/jsons/botsInVC.json'));
    if (Array.isArray(local) && local.length > 0) return local;
  } catch {
    // ignore
  }
  return null;
}

export function botsListBotKey(draft, botName) {
  if (typeof draft?.key === 'string' && draft.key.trim()) {
    return draft.key.trim();
  }
  if (typeof botName === 'string' && botName.trim()) {
    return botName.trim();
  }
  return '';
}

export function findBotsListIndexByKey(botsList, key) {
  if (!Array.isArray(botsList) || !key) return -1;
  return botsList.findIndex((item) => item?.key === key);
}

export function patchBotsListEntry(botsList, key, patch) {
  if (!Array.isArray(botsList) || !key) {
    return { botsList: Array.isArray(botsList) ? botsList : [], matched: false };
  }

  const index = findBotsListIndexByKey(botsList, key);
  if (index === -1) {
    return { botsList, matched: false };
  }

  const next = botsList.map((item, i) => {
    if (i !== index) return item;
    const updated = { ...item, ...patch };
    delete updated.approvedByPmr;
    delete updated.botType;
    if (!updated.icon) {
      delete updated.icon;
    }
    return updated;
  });
  return { botsList: next, matched: true };
}

export function syncBotsListFromDraft(botsList, draft, { domainName, botName, resolvedBotIconUrl, subjectDomain } = {}) {
  const key = botsListBotKey(draft, botName);
  if (!key) {
    return { botsList: Array.isArray(botsList) ? botsList : [], matched: false, key: '' };
  }

  const patch = {};
  const imageUrl =
    typeof resolvedBotIconUrl === 'string' && resolvedBotIconUrl
      ? resolvedBotIconUrl
      : draft?.botIconUrl;
  if (typeof imageUrl === 'string' && imageUrl && !imageUrl.startsWith('data:')) {
    patch.imageUrl = imageUrl;
  }
  if (typeof subjectDomain === 'string') {
    patch.domain = subjectDomain;
  }

  const draftHasBadgeFields =
    draft &&
    (Object.prototype.hasOwnProperty.call(draft, 'approvedByPmr') ||
      Object.prototype.hasOwnProperty.call(draft, 'pedagogical') ||
      Object.prototype.hasOwnProperty.call(draft, 'icon'));
  if (draftHasBadgeFields) {
    patch.icon = badgeIconFromFlags(draft) || undefined;
  }

  if (draft && Object.prototype.hasOwnProperty.call(draft, 'urlPath')) {
    patch.link = getBotRoutePath(domainName, botName, draft.urlPath);
  }

  if (Object.keys(patch).length === 0) {
    const matched = findBotsListIndexByKey(botsList, key) !== -1;
    return { botsList: Array.isArray(botsList) ? botsList : [], matched, key };
  }

  const result = patchBotsListEntry(botsList, key, patch);
  return { ...result, key };
}

export function buildBotsListEntry(draft, { domainName, botName, resolvedBotIconUrl, subjectDomain } = {}) {
  const key = botsListBotKey(draft, botName);
  const imageUrl =
    typeof resolvedBotIconUrl === 'string' && resolvedBotIconUrl
      ? resolvedBotIconUrl
      : draft?.botIconUrl;
  const icon = badgeIconFromFlags(draft) || (typeof draft?.icon === 'string' ? draft.icon : undefined);
  const entry = {
    name: (typeof draft?.name === 'string' && draft.name) || `${key}BotName`,
    key,
    link: getBotRoutePath(domainName, botName, draft?.urlPath),
    domain: subjectDomain || draft?.domain || '',
    titleKey: draft?.titleKey || 'teachingAssistantsTitle',
    descriptionKey:
      (typeof draft?.descriptionKey === 'string' && draft.descriptionKey) || `${key}BotDescription`,
  };
  if (typeof imageUrl === 'string' && imageUrl && !imageUrl.startsWith('data:')) {
    entry.imageUrl = imageUrl;
  }
  if (icon) {
    entry.icon = icon;
  }
  return entry;
}

export function upsertBotsListFromDraft(botsList, draft, options = {}) {
  const current = Array.isArray(botsList) ? botsList : [];
  const key = botsListBotKey(draft, options.botName);
  if (!key) {
    return { botsList: current, matched: false, key: '', added: false, removed: false };
  }
  if (draft?.activeInMyEdu !== true) {
    return {
      botsList: current.filter((item) => item?.key !== key),
      matched: findBotsListIndexByKey(current, key) !== -1,
      key,
      added: false,
      removed: true,
    };
  }
  const synced = syncBotsListFromDraft(current, draft, options);
  if (synced.matched) {
    return { ...synced, added: false, removed: false };
  }
  return {
    botsList: [...current, buildBotsListEntry(draft, options)],
    matched: true,
    key,
    added: true,
    removed: false,
  };
}
